import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface NavItem {
  key: string;
  icon: string;
  label: string;
  section: string;
  route: string;
}

export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', icon: 'dashboard', label: 'Dashboard', section: 'Overview', route: '/dashboard' },
  { key: 'catalog', icon: 'sell', label: 'Catalog', section: 'Platform', route: '/catalog' },
  { key: 'admin', icon: 'admin_panel_settings', label: 'Admin Console', section: 'Platform', route: '/admin' },
  { key: 'tenants', icon: 'business', label: 'Tenants', section: 'Management', route: '/tenants' },
  { key: 'databases', icon: 'storage', label: 'Databases', section: 'Management', route: '/databases' },
  { key: 'license', icon: 'verified_user', label: 'License', section: 'Management', route: '/license' },
  { key: 'entitlements', icon: 'vpn_key', label: 'Entitlements', section: 'Access', route: '/entitlements' },
  { key: 'seats', icon: 'group', label: 'Seats', section: 'Access', route: '/seats' },
  { key: 'overrides', icon: 'tune', label: 'Overrides', section: 'Access', route: '/overrides' },
  { key: 'usage', icon: 'bar_chart', label: 'Usage', section: 'Monitoring', route: '/usage' },
  { key: 'audit', icon: 'history', label: 'Audit Log', section: 'Monitoring', route: '/audit' },
];

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule],
  template: `
    <aside class="sidebar" [class.collapsed]="collapsed">
      <div class="brand">
        <div class="brand-icon">B</div>
        @if (!collapsed) {
          <div class="brand-text">
            <span class="brand-name">BizCombo</span>
            <span class="brand-sub">LAC Platform</span>
          </div>
        }
      </div>

      <nav class="nav">
        @for (item of navItems; track item.key) {
          @if (showSection(item)) {
            <div class="section-label" [class.center]="collapsed">
              {{ collapsed ? '·' : item.section }}
            </div>
          }
          <a class="nav-item"
             [routerLink]="item.route"
             routerLinkActive="active"
             [class.collapsed]="collapsed"
             [matTooltip]="collapsed ? item.label : ''"
             matTooltipPosition="right">
            <mat-icon class="nav-icon">{{ item.icon }}</mat-icon>
            @if (!collapsed) {
              <span class="nav-label">{{ item.label }}</span>
            }
          </a>
        }
      </nav>

      <div class="toggle-wrap">
        <button class="toggle-btn" (click)="toggleCollapse()">
          <mat-icon>{{ collapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon>
        </button>
      </div>
    </aside>
  `,
  styles: [`
    .sidebar {
      width: 220px; min-width: 220px; height: 100vh;
      background: #161b22; border-right: 1px solid #21262d;
      display: flex; flex-direction: column;
      transition: all .25s cubic-bezier(.4,0,.2,1);
    }
    .sidebar.collapsed { width: 64px; min-width: 64px; }

    .brand {
      display: flex; align-items: center; gap: 10px;
      padding: 18px 16px; height: 58px;
      border-bottom: 1px solid #21262d; overflow: hidden;
    }
    .collapsed .brand { padding: 18px 14px; justify-content: center; }
    .brand-icon {
      width: 32px; height: 32px; border-radius: 7px; flex-shrink: 0;
      background: linear-gradient(135deg, #00c1a3, #009e8f);
      display: flex; align-items: center; justify-content: center;
      font-size: 15px; color: #fff; font-weight: 700;
    }
    .brand-text { overflow: hidden; white-space: nowrap; }
    .brand-name { font-size: 14px; font-weight: 700; display: block; }
    .brand-sub {
      font-size: 9px; color: #6e7681; font-weight: 500;
      text-transform: uppercase; letter-spacing: .05em;
    }

    .nav { flex: 1; padding: 8px 6px; overflow-y: auto; overflow-x: hidden; }
    .section-label {
      font-size: 9px; font-weight: 600; color: #6e7681;
      text-transform: uppercase; letter-spacing: .07em;
      padding: 10px 12px 4px;
    }
    .section-label.center { text-align: center; padding: 10px 4px 4px; }

    .nav-item {
      display: flex; align-items: center; gap: 10px;
      padding: 8px 12px; border-radius: 6px; cursor: pointer;
      color: #8b949e; font-size: 12.5px; font-weight: 500;
      text-decoration: none; white-space: nowrap;
      transition: all .15s;
    }
    .nav-item.collapsed { padding: 8px 0; justify-content: center; }
    .nav-item:hover { color: #e6edf3; background: rgba(255,255,255,.04); }
    .nav-item.active { color: #00c1a3; background: rgba(0,193,163,.1); }
    .nav-icon { font-size: 20px; width: 22px; text-align: center; flex-shrink: 0; }

    .toggle-wrap {
      padding: 6px; border-top: 1px solid #21262d;
      display: flex; justify-content: center;
    }
    .toggle-btn {
      background: none; border: none; color: #6e7681;
      cursor: pointer; padding: 6px; border-radius: 6px;
      display: flex; align-items: center;
    }
    .toggle-btn:hover { color: #e6edf3; background: rgba(255,255,255,.04); }
  `],
})
export class SidebarComponent {
  @Input() collapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  navItems = NAV_ITEMS;
  private _lastSection = '';

  showSection(item: NavItem): boolean {
    if (item.section !== this._lastSection) {
      this._lastSection = item.section;
      return true;
    }
    return false;
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
    this._lastSection = '';
  }
}
